import clsx from 'clsx';
import React from 'react';

/**
 * Image — v2 atom.
 *
 * Presentational `<img>` wrapper. Encapsula la lógica de accesibilidad para
 * imágenes decorativas vs informativas siguiendo esta heurística:
 *
 * | Caso                                    | Marcadores DOM                              |
 * | --------------------------------------- | ------------------------------------------- |
 * | Informativa (`alt` con texto)           | `alt="…"`, sin `role`, sin `role="presentation"` |
 * | Decorativa explícita (`isDecorative`)   | `alt=""`, `role="presentation"`             |
 * | Decorativa implícita (sin `alt`,        | `alt=""`, `role="presentation"`             |
 * |  sin `role`)                            |                                             |
 * | Con `role` custom (p.ej. `"button"`)    | `alt` preservado, `role="button"`           |
 *
 * Diferencia vs v1: las imágenes decorativas se marcan con
 * `alt="" + role="presentation"` en lugar de `aria-hidden="true"` (mejora
 * a11y — el patrón recomendado por WAI-ARIA para imágenes puramente
 * decorativas).
 */

export type ImageProps = Readonly<
  {
    alt?: string;
    className?: string;
    isDecorative?: boolean;
    loading?: 'lazy' | 'eager';
    role?: string;
    src?: string;
  } & Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'alt' | 'className' | 'loading' | 'role' | 'src'>
>;

function Image({ alt = '', className, isDecorative = false, loading = 'lazy', role, src, ...rest }: ImageProps): JSX.Element {
  const treatAsDecorative = isDecorative || (alt === '' && !role);
  const altText = treatAsDecorative ? '' : alt;
  const resolvedRole = treatAsDecorative ? 'presentation' : role;

  return (
    <img
      {...rest}
      alt={altText}
      className={clsx('image', className)}
      loading={loading}
      src={src}
      {...(resolvedRole && { role: resolvedRole })}
    />
  );
}

export default Image;
